import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, AlertTriangle, Percent, TrendingDown, Ban, CheckCircle } from 'lucide-react';

const WhatIsGAPInsurancePage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Blue Lion Law</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                What Is GAP Insurance — and How Was It Mis-Sold?
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                Understanding the scandal that could mean you're owed compensation
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-foreground leading-relaxed mb-8">
                Guaranteed Asset Protection (GAP) insurance is designed to cover the difference between 
                what your insurer pays out if your car is written off, and what you still owe on your 
                finance agreement. In principle, it's a perfectly valid product — but, as with PPI, 
                it was the <strong>way it was sold</strong> that turned it into a ticking time bomb.
              </p>
            </div>
          </div>
        </section>

        {/* What We Know Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                What We Now Know
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                The truth about GAP insurance sales practices has finally come to light, and what we're seeing is deeply troubling:
              </p>
              
              <div className="space-y-6">
                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <p className="text-foreground">
                      <strong>GAP insurance was frequently added to finance agreements</strong>, or paid for 
                      via customer deposits at the point of sale — often without proper explanation.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <p className="text-foreground">
                      <strong>Consumers weren't told that dealers were earning huge commissions</strong> for 
                      selling the policy. In most cases we've seen, this was never disclosed.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-destructive">
                  <CardContent className="p-6">
                    <p className="text-foreground">
                      <strong>Those commissions often made up around 70% of the entire policy premium.</strong> 
                      That's not just a markup — it's financial daylight robbery disguised as protection.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* PPI Echo Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The PPI Echo: Greed, Misleading Sales, and Hidden Commission
                </h2>
              </div>
              
              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  For those who remember the PPI scandal, this all sounds eerily familiar. At the heart of 
                  PPI mis-selling was the non-disclosure of commissions and the incentive for salespeople 
                  to push a product that suited their bonus, not the customer.
                </p>
                <p>
                  It now appears that <strong className="text-foreground">GAP insurance followed the same playbook</strong>. 
                  Despite the public embarrassment and regulatory scrutiny that followed PPI, the finance 
                  sector clearly learned little — if anything.
                </p>
                <p>
                  What we're seeing with GAP insurance is a damning indication that weak regulation and 
                  institutional greed have once again taken precedence over fairness and transparency.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 70% Commission Highlight */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <Percent className="w-8 h-8 text-gold" />
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground">
                  70% Commission? That's Not Protection — That's Profiteering
                </h2>
              </div>
              
              <p className="text-lg text-primary-foreground/80 mb-8">
                From the cases we've reviewed, it's evident that the average commission earned on GAP 
                policies sits around <strong className="text-gold">70% of the policy cost</strong>.
              </p>

              <div className="bg-primary-foreground/10 rounded-2xl p-8 mb-8">
                <p className="text-2xl font-bold text-primary-foreground mb-4">
                  Let that sink in:
                </p>
                <p className="text-xl text-primary-foreground/90">
                  For every <span className="text-gold font-bold">£300</span> you paid for GAP cover, 
                  the dealership or lender may have pocketed <span className="text-gold font-bold">£210</span>.
                </p>
              </div>

              <p className="text-lg text-primary-foreground/80">
                How can a policy be "value for money" when over two-thirds of the premium disappears in 
                undisclosed commission before any actual cover is provided?
              </p>
              <p className="text-lg text-primary-foreground/90 mt-4">
                The issue isn't GAP insurance itself — it can be a valuable product. 
                <strong> The problem lies in the greedy and opaque practices used to sell it.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* FCA Action Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Ban className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The FCA Has Finally Acted
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                The Financial Conduct Authority (FCA) has now intervened. Their Value Measures Data report 
                found shocking statistics:
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <Card className="text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-accent mb-2">6%</div>
                    <p className="text-sm text-muted-foreground">
                      of premiums paid by customers were returned in claims
                    </p>
                  </CardContent>
                </Card>
                <Card className="text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-gold mb-2">70%</div>
                    <p className="text-sm text-muted-foreground">
                      of premiums went to dealers as commission
                    </p>
                  </CardContent>
                </Card>
                <Card className="text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold text-destructive mb-2">80%</div>
                    <p className="text-sm text-muted-foreground">
                      of providers have suspended sales following FCA pressure
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-muted/50">
                <CardContent className="p-6">
                  <p className="text-foreground italic">
                    Sheldon Mills, the FCA's executive director of Consumers and Competition, stated clearly 
                    that GAP insurance in its current form <strong>"does not provide fair value"</strong> to consumers.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Most Never Claim */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The Truth About Redress — and Why Most Victims Never Claim
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                Even with the PPI scandal — the most well-publicised mis-selling event in UK history — 
                <strong className="text-foreground"> millions of valid claims were never submitted</strong>. 
                Many consumers either:
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-destructive/10 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-destructive text-sm">✗</span>
                  </div>
                  <span className="text-lg text-muted-foreground">Didn't know they had a claim</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-destructive/10 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-destructive text-sm">✗</span>
                  </div>
                  <span className="text-lg text-muted-foreground">Missed the deadline</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-destructive/10 flex items-center justify-center mt-1 flex-shrink-0">
                    <span className="text-destructive text-sm">✗</span>
                  </div>
                  <span className="text-lg text-muted-foreground">Were wrongly told they weren't eligible</span>
                </li>
              </ul>

              <Card className="border-gold bg-gold/5">
                <CardContent className="p-6">
                  <p className="text-foreground font-medium">
                    Despite paying out tens of billions in compensation, lenders still walked away profitable. 
                    The system was stacked in their favour then — and, unless people act now, 
                    <strong> history will repeat itself.</strong>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Final Thoughts */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                The Product Isn't the Problem — It's How It Was Sold
              </h2>
              
              <div className="space-y-6 text-lg text-muted-foreground mb-8">
                <p>
                  GAP insurance, like PPI before it, wasn't inherently bad. <strong className="text-foreground">It's the 
                  commission culture, lack of transparency, and failure to treat customers fairly</strong> that 
                  turned a useful product into a scandal-in-waiting.
                </p>
                <p>
                  Once again, the finance industry has shown that profit comes before principle — unless 
                  they're held to account.
                </p>
              </div>

              <Card className="bg-accent/5 border-accent">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-foreground font-medium mb-2">What Can You Do?</p>
                      <p className="text-muted-foreground">
                        If you purchased GAP insurance as part of a car finance deal, there's a real chance that 
                        you paid not only for the policy, but also a significant undisclosed commission — and you 
                        may be entitled to compensation for the unfair relationship that created.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Think You May Have a Claim?
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                Blue Lion Law can assess your individual circumstances. Eligibility depends on your specific situation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                    Find Your Agreements
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
                  <a href="tel:02081530033">
                    Call 0208 153 0033
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background border-t border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <a href="/gap-insurance/how-gap-is-sold-with-pcp" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">How GAP Insurance is Sold with PCP</span>
                </a>
                <a href="/gap-insurance/how-to-check-if-gap-was-mis-sold" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">How to Check if GAP was Mis-sold</span>
                </a>
                <a href="/gap-insurance-claims" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">GAP Insurance Claims</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default WhatIsGAPInsurancePage;
